"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="234037" className="flex gap-4">
        <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
        <div className="h-9 w-20 animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative flex items-center justify-center bg-transparent px-4 py-2 text-base font-medium tracking-tight text-foreground transition-colors hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary data-[active=true]:bg-transparent data-[active=true]:text-primary">
                {item.label?.replace("Auto Berles", "Autó Bérlés")}
                <span className="absolute bottom-1 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-primary transition-all duration-300 ease-out group-hover:w-[calc(100%-2rem)]" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="mt-8 flex flex-col gap-4 px-4">
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
        <div className="h-10 w-full animate-pulse rounded-md bg-muted" />
      </div>;
  }
  return <div className="mt-8 flex flex-col gap-6 px-4">
      <nav className="flex flex-col gap-4">
        {menuItems.map((item, i) => <div key={item.id} data-index={i}>
            <Link to={item.href || "#"} className="block text-xl font-medium tracking-tight text-foreground transition-colors hover:text-primary">
              {item.label?.replace("Auto Berles", "Autó Bérlés")}
            </Link>
          </div>)}
      </nav>
      <div className="mt-4">
        <div className="group relative inline-flex w-full">
          <div className="absolute -inset-[1px] animate-pulse rounded-lg bg-primary opacity-50 transition-opacity duration-300 group-hover:opacity-100" />
          <Button asChild className="relative w-full border border-transparent bg-background text-foreground rounded-lg hover:bg-background/90 font-default text-base">
            <Link to="#">Foglaljon Most</Link>
          </Button>
        </div>
      </div>
    </div>;
}
function HeaderContent() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header className={`sticky top-0 z-50 w-full border-b transition-all duration-300 ${isScrolled ? 'border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60' : 'border-transparent bg-transparent'}`}>
      <div className="container mx-auto flex items-center justify-between gap-6 px-4 py-4">
        <div className="flex items-center justify-start">
          <Link to="/">
            <WvcLogo className="h-7 w-auto" />
          </Link>
        </div>

        <div id="google_translate_element" className="flex items-center gap-2 md:ml-6" />
        
        <nav className="hidden md:flex items-center gap-8">
          <HeaderMenuConsumer />
        </nav>
        
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full">
              <SheetTitle>Menu</SheetTitle>
              <MobileMenuConsumer />
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>;
}
export default function Header() {
  return <MenuProvider>
      <HeaderContent />
    </MenuProvider>;
}